import React, { useState, useRef, useEffect, useMemo } from 'react';
import { BsChevronRight, BsChevronLeft, BsChevronDown, BsLightningChargeFill } from "react-icons/bs";
import { IoMdCheckmark } from "react-icons/io";
import { FaClock, FaCalendarAlt } from "react-icons/fa";
import { Select, SelectItem, Button } from "@heroui/react";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const startOfDayLocal = (d) => {
    const newDate = new Date(d);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
};

const TODAY = startOfDayLocal(new Date());
const MIN_DATE = new Date(2020, 0, 1);

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getDate() === d2.getDate() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getFullYear() === d2.getFullYear();
};

const isInRange = (date, rangeStart, rangeEnd) => {
    if (!rangeStart || !rangeEnd) return false;
    const ts = startOfDayLocal(date).getTime();
    const s = startOfDayLocal(rangeStart).getTime();
    const e = startOfDayLocal(rangeEnd).getTime();
    return ts > Math.min(s, e) && ts < Math.max(s, e);
};

const isRangeStart = (date, rangeStart) => {
    if (!rangeStart) return false;
    return isSameDay(date, rangeStart);
};

const isRangeEnd = (date, rangeEnd) => {
    if (!rangeEnd) return false;
    return isSameDay(date, rangeEnd);
};

const animationStyles = `
  @keyframes slideIn {
    from { opacity: 0; transform: translateY(-10px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  .animate-slide-in {
    animation: slideIn 0.2s ease-out forwards;
  }
`;

/* ── Custom dropdown select ── */
function CustomSelect({ value, options, onChange, minWidth = "min-w-[100px]" }) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);

    return (
        <div className={`relative ${minWidth}`} ref={containerRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center justify-between w-full bg-background border text-default-700 py-1 pl-3 pr-2 rounded-md shadow-sm text-sm font-medium transition-all duration-200
                ${isOpen ? 'border-success ring-1 ring-success text-success' : 'border-default-300 hover:border-default-400'}`}
            >
                <span className="truncate mr-2">{selectedOption?.label}</span>
                <BsChevronDown className={`w-3 h-3 text-default-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-white dark:bg-content1 border border-default-200 rounded-lg shadow-lg z-[100] py-1 animate-slide-in origin-top">
                    {options.map((opt) => (
                        <button
                            key={opt.value}
                            disabled={opt.disabled}
                            onClick={() => {
                                if (!opt.disabled) {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }
                            }}
                            className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-all duration-200
                                ${opt.value === value ? 'text-success font-semibold bg-success/10' : 'text-default-700'}
                                ${opt.disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-success/10 hover:text-success'}
                            `}
                        >
                            <span>{opt.label}</span>
                            {opt.value === value && <IoMdCheckmark className="w-3 h-3" />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/* ── Range-aware Day Picker ── */
function RangeDayPicker({ viewDate, rangeStart, rangeEnd, onDayClick }) {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getFirstDayOfMonth(year, month);
    const prevMonthDays = getDaysInMonth(year, month - 1);
    const fillerStart = prevMonthDays - startDay + 1;

    const days = [];

    for (let i = 0; i < startDay; i++) {
        days.push({ day: fillerStart + i, type: 'prev', date: new Date(year, month - 1, fillerStart + i) });
    }
    for (let i = 1; i <= daysInMonth; i++) {
        days.push({ day: i, type: 'current', date: new Date(year, month, i) });
    }
    // Only pad enough next-month days to complete the last row (max 7 cols)
    const leftover = days.length % 7;
    if (leftover > 0) {
        const needed = 7 - leftover;
        for (let i = 1; i <= needed; i++) {
            days.push({ day: i, type: 'next', date: new Date(year, month + 1, i) });
        }
    }

    const isDisabled = (date) => date > TODAY || date < MIN_DATE;

    const effectiveEnd = rangeEnd;

    // Class for the WRAPPER div — draws the continuous range band
    const getDayWrapperClass = (dateObj) => {
        const date = dateObj.date;
        const isOtherMonth = dateObj.type !== 'current';
        if (isOtherMonth || isDisabled(date)) return "flex justify-center";

        const isStart = isRangeStart(date, rangeStart);
        const isEnd = isRangeEnd(date, effectiveEnd);
        const inRange = isInRange(date, rangeStart, effectiveEnd);

        let cls = "flex justify-center ";

        if (isStart && isEnd) {
            return cls;
        }
        if (isStart) {
            return cls + "bg-gradient-to-r from-transparent from-50% to-success/10 to-50%";
        }
        if (isEnd) {
            return cls + "bg-gradient-to-l from-transparent from-50% to-success/10 to-50%";
        }
        if (inRange) {
            return cls + "bg-success/10";
        }

        return cls;
    };

    // Class for the BUTTON — circle for start/end, plain for in-range
    const getDayClass = (dateObj) => {
        const date = dateObj.date;
        const disabled = isDisabled(date);
        const isOtherMonth = dateObj.type !== 'current';

        let base = "h-9 w-9 flex items-center justify-center text-sm transition-all duration-150 ease-in-out relative z-0 rounded-full ";

        if (isOtherMonth) {
            return base + "text-default-300 cursor-default";
        }

        if (disabled) return base + "text-default-300 cursor-not-allowed";

        const isStart = isRangeStart(date, rangeStart);
        const isEnd = isRangeEnd(date, effectiveEnd);
        const inRange = isInRange(date, rangeStart, effectiveEnd);
        const isToday = isSameDay(date, TODAY);

        base += "cursor-pointer ";

        if (isStart || isEnd) {
            return base + "bg-success text-white font-semibold shadow-md";
        }
        if (inRange) {
            return base + "text-default-700";
        }
        if (isToday) return base + "border border-success font-medium text-success";

        return base + "text-default-700 hover:bg-default-100";
    };

    return (
        <div className="relative z-0">
            <div className="grid grid-cols-7 mb-2">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="text-center text-xs text-default-400 font-medium py-1">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-y-4">
                {days.map((d, i) => (
                    <div key={i} className={getDayWrapperClass(d)}>
                        <button
                            onClick={() => d.type === 'current' && !isDisabled(d.date) && onDayClick(d.date)}
                            className={getDayClass(d)}
                            disabled={d.type !== 'current' || isDisabled(d.date)}
                        >
                            {d.day}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ── Single Calendar Panel ── */
function CalendarPanel({ viewDate, setViewDate, rangeStart, rangeEnd, onDayClick, label }) {
    const canNextMonth = () =>
        viewDate.getFullYear() < TODAY.getFullYear() ||
        (viewDate.getFullYear() === TODAY.getFullYear() && viewDate.getMonth() < TODAY.getMonth());
    const canPrevMonth = () =>
        viewDate.getFullYear() > MIN_DATE.getFullYear() ||
        (viewDate.getFullYear() === MIN_DATE.getFullYear() && viewDate.getMonth() > MIN_DATE.getMonth());

    const changeMonth = (offset) => {
        const d = new Date(viewDate);
        d.setMonth(d.getMonth() + offset);
        setViewDate(d);
    };

    const monthOptions = MONTH_NAMES.map((name, i) => ({
        label: name,
        value: i,
        disabled: viewDate.getFullYear() === TODAY.getFullYear() && i > TODAY.getMonth()
    }));

    const yearOptions = Array.from({ length: TODAY.getFullYear() - 2020 + 1 }, (_, i) => 2020 + i).map(y => ({
        label: y.toString(),
        value: y,
        disabled: false
    }));

    return (
        <div className="flex flex-col gap-3 flex-1 min-w-[280px]">
            <p className="text-xs font-semibold text-default-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-center justify-between gap-2 relative z-50">
                <button
                    onClick={() => changeMonth(-1)}
                    disabled={!canPrevMonth()}
                    className="p-1.5 hover:bg-default-100 rounded-md text-default-600 disabled:opacity-30 transition-colors"
                >
                    <BsChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex items-center gap-2 flex-1 justify-center z-50">
                    <CustomSelect
                        value={viewDate.getMonth()}
                        options={monthOptions}
                        onChange={(val) => {
                            const d = new Date(viewDate);
                            d.setMonth(val);
                            setViewDate(d);
                        }}
                        minWidth="min-w-[110px]"
                    />
                    <CustomSelect
                        value={viewDate.getFullYear()}
                        options={yearOptions}
                        onChange={(val) => {
                            const d = new Date(viewDate);
                            d.setFullYear(val);
                            setViewDate(d);
                        }}
                        minWidth="min-w-[85px]"
                    />
                </div>

                <button
                    onClick={() => changeMonth(1)}
                    disabled={!canNextMonth()}
                    className="p-1.5 hover:bg-default-100 rounded-md text-default-600 disabled:opacity-30 transition-colors"
                >
                    <BsChevronRight className="w-4 h-4" />
                </button>
            </div>

            <RangeDayPicker
                viewDate={viewDate}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                onDayClick={onDayClick}
            />
        </div>
    );
}

/* ══════════════════════════════════════════
   MAIN — CustomDateRangePicker
   ══════════════════════════════════════════ */
export default function CustomDateRangePicker({ onRangeChange }) {
    // ── Default: start of today (UTC 00:00) for start, end of today (UTC 23:59) for end ──
    const getStartOfDayUTC = () => {
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        return d;
    };

    const getEndOfDayUTC = () => {
        const d = new Date();
        d.setUTCHours(23, 59, 59, 999);
        return d;
    };

    // Calendar view dates (left/right)
    const [leftViewDate, setLeftViewDate] = useState(() => {
        const d = new Date(TODAY);
        d.setMonth(d.getMonth() - 1);
        return d;
    });
    const [rightViewDate, setRightViewDate] = useState(TODAY);

    // Range selection
    const [rangeStart, setRangeStart] = useState(TODAY);
    const [rangeEnd, setRangeEnd] = useState(TODAY);
    const [selectingEnd, setSelectingEnd] = useState(false); // false = picking start, true = picking end

    // Time state — start time defaults to 00:00 UTC, end time to 23:59 UTC
    const [startHour, setStartHour] = useState("00");
    const [startMinute, setStartMinute] = useState("00");
    const [endHour, setEndHour] = useState("23");
    const [endMinute, setEndMinute] = useState("59");

    const hoursOptions = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
    const minutesOptions = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];
    // Add "59" to minutes if not present (for end-of-day)
    const endMinutesOptions = [...minutesOptions, "59"].sort((a, b) => Number(a) - Number(b));

    // ── Day click handler ──
    const handleDayClick = (date) => {
        if (!selectingEnd) {
            // Picking start date
            setRangeStart(date);
            setRangeEnd(null);
            setSelectingEnd(true);
        } else {
            // Picking end date
            if (date < rangeStart) {
                // Clicked before start — swap
                setRangeEnd(rangeStart);
                setRangeStart(date);
            } else {
                setRangeEnd(date);
            }
            setSelectingEnd(false);
        }
    };



    // ── Full Day button ──
    const handleFullDay = () => {
        setRangeStart(TODAY);
        setRangeEnd(TODAY);
        setStartHour("00");
        setStartMinute("00");
        setEndHour("23");
        setEndMinute("59");
        setSelectingEnd(false);
        // Set left calendar to previous month, right to current
        const prevMonth = new Date(TODAY);
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        setLeftViewDate(prevMonth);
        setRightViewDate(new Date(TODAY));
    };

    // ── Emit combined ISO strings ──
    useEffect(() => {
        if (rangeStart && rangeEnd && typeof onRangeChange === 'function') {
            // Build start datetime in UTC
            const sDate = new Date(Date.UTC(
                rangeStart.getFullYear(),
                rangeStart.getMonth(),
                rangeStart.getDate(),
                parseInt(startHour, 10),
                parseInt(startMinute, 10),
                0, 0
            ));
            // Build end datetime in UTC
            const eDate = new Date(Date.UTC(
                rangeEnd.getFullYear(),
                rangeEnd.getMonth(),
                rangeEnd.getDate(),
                parseInt(endHour, 10),
                parseInt(endMinute, 10),
                59, 999
            ));

            onRangeChange({
                startDate: sDate.toISOString(),
                endDate: eDate.toISOString(),
            });
        }
    }, [rangeStart, rangeEnd, startHour, startMinute, endHour, endMinute, onRangeChange]);

    // ── Format selected date for display ──
    const formatDateLabel = (date) => {
        if (!date) return "—";
        return date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    return (
        <div className="flex flex-col gap-5 w-full p-6 border border-default-200 rounded-2xl bg-white shadow-md">
            <style>{animationStyles}</style>

            {/* ── Header row with Full Day button ── */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <label className="text-base font-semibold text-default-700 flex items-center gap-2">
                    <FaCalendarAlt className="text-success" /> Select Date Range
                </label>
                <Button
                    size="sm"
                    color="success"
                    variant="flat"
                    startContent={<BsLightningChargeFill size={14} />}
                    onPress={handleFullDay}
                    className="font-semibold"
                >
                    Full Day (Today)
                </Button>
            </div>

            {/* ── Selection status chips ── */}
            <div className="flex items-center gap-3 text-xs flex-wrap">
                <div className={`px-3 py-1.5 rounded-full font-medium border transition-all ${!selectingEnd
                    ? 'border-success bg-success/10 text-success'
                    : 'border-default-200 bg-default-50 text-default-600'
                    }`}>
                    📅 Start: <strong>{formatDateLabel(rangeStart)}</strong>
                </div>
                <span className="text-default-300 text-lg">→</span>
                <div className={`px-3 py-1.5 rounded-full font-medium border transition-all ${selectingEnd
                    ? 'border-success bg-success/10 text-success'
                    : 'border-default-200 bg-default-50 text-default-600'
                    }`}>
                    📅 End: <strong>{rangeEnd ? formatDateLabel(rangeEnd) : "Click a date…"}</strong>
                </div>
            </div>

            {/* ── Dual Calendar ── */}
            <div className="flex flex-col md:flex-row gap-6">
                <CalendarPanel
                    viewDate={leftViewDate}
                    setViewDate={setLeftViewDate}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    onDayClick={handleDayClick}
                    label="From"
                />
                <div className="hidden md:block w-px bg-default-200 self-stretch" />
                <CalendarPanel
                    viewDate={rightViewDate}
                    setViewDate={setRightViewDate}
                    rangeStart={rangeStart}
                    rangeEnd={rangeEnd}
                    onDayClick={handleDayClick}
                    label="To"
                />
            </div>

            <hr className="border-default-200" />

            {/* ── Time Pickers (separate for start & end) ── */}
            <div className="flex flex-col md:flex-row gap-6">
                {/* Start Time */}
                <div className="flex flex-col gap-3 flex-1">
                    <label className="text-sm font-semibold text-default-700 flex items-center gap-2">
                        <FaClock className="text-success" /> Start Time
                    </label>
                    <div className="flex gap-3">
                        <Select
                            label="Hour"
                            variant="faded"
                            size="sm"
                            selectedKeys={startHour ? [startHour] : []}
                            onChange={(e) => setStartHour(e.target.value)}
                            className="flex-1"
                        >
                            {hoursOptions.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </Select>
                        <Select
                            label="Minute"
                            variant="faded"
                            size="sm"
                            selectedKeys={startMinute ? [startMinute] : []}
                            onChange={(e) => setStartMinute(e.target.value)}
                            className="flex-1"
                        >
                            {minutesOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </Select>
                    </div>
                </div>

                <div className="hidden md:block w-px bg-default-200 self-stretch" />

                {/* End Time */}
                <div className="flex flex-col gap-3 flex-1">
                    <label className="text-sm font-semibold text-default-700 flex items-center gap-2">
                        <FaClock className="text-success" /> End Time
                    </label>
                    <div className="flex gap-3">
                        <Select
                            label="Hour"
                            variant="faded"
                            size="sm"
                            selectedKeys={endHour ? [endHour] : []}
                            onChange={(e) => setEndHour(e.target.value)}
                            className="flex-1"
                        >
                            {hoursOptions.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                        </Select>
                        <Select
                            label="Minute"
                            variant="faded"
                            size="sm"
                            selectedKeys={endMinute ? [endMinute] : []}
                            onChange={(e) => setEndMinute(e.target.value)}
                            className="flex-1"
                        >
                            {endMinutesOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </Select>
                    </div>
                </div>
            </div>
        </div>
    );
}
